import { useCallback } from 'react';

import moment from 'moment';

import {
  EmployeesSchedules,
  PersonAbsence,
  Shift,
  TimelineItem,
  TimelineScale,
  TreeNode
} from '../../../types/interfaces';
import { CalendarViewMode, CustomTimelineItem } from '../types';

import { SelectedTaskInfo } from './useCalendarAssignmentsDialogsState';

export interface CalendarAssignmentsInteractionData {
  employeesShifts: EmployeesSchedules | null;
  employeesAbsences: Record<string, Record<string, PersonAbsence[]>>;
  newAbsences: PersonAbsence[];
  updatedAbsences: PersonAbsence[];
  newShifts: Shift[];
  updatedShifts: Shift[];
  timelineItems: TimelineItem[];
}

export interface CalendarAssignmentsInteractionViewState {
  viewMode: CalendarViewMode;
  timelineScale: TimelineScale;
  selectedDepartmentFilter: TreeNode | null;
  selectedDateFilter: Date | null;
  selectedItemId?: string;
}

export interface CalendarAssignmentsDialogActions {
  handleSaveShift: (shift: Shift) => Promise<boolean>;
  handleUpdateAbsence: (absence: PersonAbsence) => boolean;
  setShiftDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setAbsenceDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTaskInfo: React.Dispatch<React.SetStateAction<SelectedTaskInfo>>;
  setShiftData: React.Dispatch<React.SetStateAction<Shift | null>>;
  setAbsenceData: React.Dispatch<React.SetStateAction<PersonAbsence | null>>;
  setSelectedPersonId: React.Dispatch<React.SetStateAction<number | null>>;
  setSelectedDate: React.Dispatch<React.SetStateAction<moment.Moment | null>>;
  setChoiceDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
  openNewDialog: (
    type: 'shift' | 'absence' | 'task',
    personId?: number,
    date?: moment.Moment
  ) => void;
}

export interface UseCalendarAssignmentsInteractionsArgs {
  data: CalendarAssignmentsInteractionData;
  view: CalendarAssignmentsInteractionViewState;
  dialogActions: CalendarAssignmentsDialogActions;
}

export function useCalendarAssignmentsInteractions({
  data,
  view,
  dialogActions
}: UseCalendarAssignmentsInteractionsArgs) {
  const findShift = useCallback(
    (guid: string): Shift | undefined => {
      const updated = data.updatedShifts.find((shift) => shift.guid === guid);
      if (updated) return updated;

      const newShift = data.newShifts.find((shift) => shift.guid === guid);
      if (newShift) return newShift;

      if (data.employeesShifts) {
        for (const [personId, schedules] of Object.entries(
          data.employeesShifts
        )) {
          for (const [date, daySchedules] of Object.entries(schedules)) {
            const found = daySchedules.find((shift) => shift.guid === guid);
            if (found) {
              return {
                ...found,
                personId: parseInt(personId, 10),
                date,
                breakDuration: found.break
              };
            }
          }
        }
      }
      return undefined;
    },
    [data.employeesShifts, data.newShifts, data.updatedShifts]
  );

  const findAbsence = useCallback(
    (id: string): PersonAbsence | undefined => {
      const allAbsences = [
        ...Object.values(data.employeesAbsences).flatMap((dates) =>
          Object.values(dates).flat()
        ),
        ...data.newAbsences,
        ...data.updatedAbsences
      ];
      return allAbsences.find(
        (absence) => `${absence.personId}-absence-${absence.from}` === id
      );
    },
    [data.employeesAbsences, data.newAbsences, data.updatedAbsences]
  );

  const onSaveShift = useCallback(
    async (shift: Shift) => {
      const success = await dialogActions.handleSaveShift(shift);
      if (success) {
        dialogActions.setShiftDialogVisible(false);
      }
    },
    [dialogActions]
  );

  const onSaveAbsence = useCallback(
    (absence: PersonAbsence) => {
      const success = dialogActions.handleUpdateAbsence(absence);
      if (success) {
        dialogActions.setAbsenceDialogVisible(false);
      }
    },
    [dialogActions]
  );

  const onTaskClick = useCallback((taskId: number) => {
    window.open(`/${taskId}`, '_blank');
  }, []);

  const handleItemDoubleClick = useCallback(
    (itemId: string | number) => {
      if (view.viewMode === 'timesheet') return;

      const item = data.timelineItems.find(
        (timelineItem) => timelineItem.id === itemId
      ) as CustomTimelineItem | undefined;

      if (
        item?.data?.type === 'task' &&
        view.timelineScale === TimelineScale.MONTH
      ) {
        dialogActions.setSelectedTaskInfo({
          personId: item.data.personId!,
          day: item.data.day!,
          tasks: item.data.tasks!
        });
        return;
      }

      const idString = itemId.toString();
      const shift = findShift(idString);
      if (shift) {
        dialogActions.setShiftData({
          ...shift,
          departmentId: Number(view.selectedDepartmentFilter?.key)
        });
        dialogActions.setShiftDialogVisible(true);
      } else if (idString.includes('-absence-')) {
        const absence = findAbsence(idString);
        if (absence) {
          dialogActions.setAbsenceData(absence);
          dialogActions.setAbsenceDialogVisible(true);
        }
      } else if (idString.includes('-bg-')) {
        const [idPart, dateKey] = idString.split('-bg-');
        const personId = parseInt(idPart, 10);
        if (!isNaN(personId)) {
          dialogActions.setSelectedPersonId(personId);
          dialogActions.setSelectedDate(moment(dateKey, 'YYYY-MM-DD'));
          dialogActions.setChoiceDialogVisible(true);
        }
      } else if (idString.includes('task-')) {
        const taskId = idString.split('-task-')[1];
        onTaskClick(parseInt(taskId));
      }
    },
    [
      data.timelineItems,
      dialogActions,
      findAbsence,
      findShift,
      onTaskClick,
      view.selectedDepartmentFilter,
      view.timelineScale,
      view.viewMode
    ]
  );

  const onCreateTask = useCallback(
    (personId?: number) => {
      const params = new URLSearchParams();
      params.append(
        'departmentId',
        view.selectedDepartmentFilter?.key?.toString() || ''
      );

      if (personId) {
        params.append('employeeId', personId.toString());
      }

      window.open(`/newTask?${params.toString()}`, '_blank');
    },
    [view.selectedDepartmentFilter]
  );

  const handleAddFromHeader = useCallback(
    (actionType: 'shift' | 'absence' | 'task') => {
      if (actionType === 'task') {
        onCreateTask();
        return;
      }

      let personId: number | undefined;
      let date: moment.Moment | undefined;

      if (view.selectedItemId) {
        const shift = findShift(view.selectedItemId);
        if (shift) {
          personId = shift.personId;
          date = moment(shift.date, 'YYYY-MM-DD');
        } else if (view.selectedItemId.includes('-bg-')) {
          const [employeeId, dateKey] = view.selectedItemId.split('-bg-');
          personId = parseInt(employeeId, 10);
          if (!isNaN(personId)) {
            date = moment(dateKey, 'YYYY-MM-DD');
          }
        }
      }

      if (!date || !date.isValid()) {
        const now = moment();
        const selected = moment(view.selectedDateFilter);
        const isCurrentMonth =
          selected.isSame(now, 'month') && selected.isSame(now, 'year');
        date = isCurrentMonth ? now : selected.startOf('month');
      }

      dialogActions.openNewDialog(actionType, personId, date);
    },
    [
      dialogActions,
      findShift,
      onCreateTask,
      view.selectedDateFilter,
      view.selectedItemId
    ]
  );

  return {
    onSaveShift,
    onSaveAbsence,
    onTaskClick,
    onCreateTask,
    handleItemDoubleClick,
    handleAddFromHeader
  };
}
